import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Bell, Clock, Calendar, FileText, ClipboardList, X, ChevronRight, CheckCircle2 } from 'lucide-react';

function NotificationCenter() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [dismissedIds, setDismissedIds] = useState([]);

  useEffect(() => {
    fetchNotifications();
    // Refresh every 5 minutes
    const interval = setInterval(fetchNotifications, 300000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await api.get('/notifications/summary');
      setData(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = (id) => {
    setDismissedIds(prev => [...prev, id]);
  };

  const getSeverityStyles = (severity) => {
    const styles = {
      critical: 'bg-red-50 border-red-200 text-red-800',
      high: 'bg-orange-50 border-orange-200 text-orange-800',
      medium: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      low: 'bg-blue-50 border-blue-200 text-blue-800',
      info: 'bg-purple-50 border-purple-200 text-purple-800',
    };
    return styles[severity] || styles.info;
  };

  const getIcon = (iconType) => {
    const icons = {
      clock: <Clock className="w-4 h-4" />,
      calendar: <Calendar className="w-4 h-4" />,
      file: <FileText className="w-4 h-4" />,
      clipboard: <ClipboardList className="w-4 h-4" />,
    };
    return icons[iconType] || <Bell className="w-4 h-4" />;
  };

  const getTypeLabel = (type) => {
    const labels = {
      task_overdue: 'Tarefa',
      task_upcoming: 'Tarefa',
      protocol_upcoming: 'Protocolo',
      protocol_past: 'Protocolo',
      license_expiring: 'Licença',
    };
    return labels[type] || 'Alerta';
  };

  const visibleNotifications = data?.notifications?.filter(n => !dismissedIds.includes(n.id)) || [];
  const urgentCount = visibleNotifications.filter(n => ['critical', 'high'].includes(n.severity)).length;
  const totalCount = visibleNotifications.length;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notifications-button">
          <Bell className="w-6 h-6 text-gray-700" />
          {totalCount > 0 && (
            <span className={`absolute -top-1 -right-1 min-w-[20px] h-5 px-1 text-white text-xs rounded-full flex items-center justify-center ${urgentCount > 0 ? 'bg-red-500' : 'bg-emerald-600'}`}>
              {totalCount > 99 ? '99+' : totalCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[420px] max-h-[500px] overflow-hidden p-0" align="end">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 z-10">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Central de Avisos
            </h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchNotifications}
              className="text-gray-500 hover:text-gray-700"
            >
              Atualizar
            </Button>
          </div>
          
          {/* Summary badges */}
          {data?.summary && totalCount > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {data.summary.tasks_overdue > 0 && (
                <Badge className="bg-red-100 text-red-700 border-red-200">
                  {data.summary.tasks_overdue} atrasada{data.summary.tasks_overdue > 1 ? 's' : ''}
                </Badge>
              )}
              {data.summary.tasks_upcoming > 0 && (
                <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                  {data.summary.tasks_upcoming} esta semana
                </Badge>
              )}
              {data.summary.protocols > 0 && (
                <Badge className="bg-purple-100 text-purple-700 border-purple-200">
                  {data.summary.protocols} protocolo{data.summary.protocols > 1 ? 's' : ''}
                </Badge>
              )}
              {data.summary.licenses > 0 && (
                <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
                  {data.summary.licenses} licença{data.summary.licenses > 1 ? 's' : ''}
                </Badge>
              )}
            </div>
          )}
        </div>

        <div className="overflow-y-auto max-h-[380px]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-800"></div>
            </div>
          ) : visibleNotifications.length === 0 ? (
            <div className="text-center py-12 px-4">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
              <p className="text-gray-600 font-medium">Tudo em dia!</p>
              <p className="text-sm text-gray-400 mt-1">Nenhum aviso no momento</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {visibleNotifications.map((notification) => (
                <div 
                  key={notification.id}
                  className={`p-3 hover:bg-gray-50 transition-colors ${getSeverityStyles(notification.severity)}`}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${
                      notification.severity === 'critical' ? 'bg-red-200' :
                      notification.severity === 'high' ? 'bg-orange-200' :
                      notification.severity === 'medium' ? 'bg-yellow-200' :
                      notification.severity === 'low' ? 'bg-blue-200' :
                      'bg-purple-200'
                    }`}>
                      {getIcon(notification.icon)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge variant="outline" className="text-xs py-0">
                          {getTypeLabel(notification.type)}
                        </Badge>
                        {notification.project_name && (
                          <span className="text-xs text-gray-500 truncate">
                            {notification.project_name}
                          </span>
                        )}
                      </div>
                      <p className="text-sm leading-relaxed">{notification.message}</p>
                      <div className="flex items-center justify-between mt-2">
                        <Link 
                          to={notification.link}
                          onClick={() => setIsOpen(false)}
                          className="text-xs text-emerald-700 hover:text-emerald-800 font-medium flex items-center gap-1"
                        >
                          Ver detalhes
                          <ChevronRight className="w-3 h-3" />
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                          onClick={() => handleDismiss(notification.id)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {visibleNotifications.length > 0 && (
          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-3">
            <Link 
              to="/task-calendar"
              onClick={() => setIsOpen(false)}
              className="block text-center text-sm text-emerald-700 hover:text-emerald-800 font-medium"
            >
              Ver calendário completo
            </Link>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export default NotificationCenter;
